import React, { useState, useEffect, FormEvent } from 'react'
import api from '../api-client'
import { toast } from 'react-toastify'
import { EQUIPMENT_TYPES_LABELS, initialTenantProduct, IProduct, toDateTimeLocalInputString } from '@oneethos/shared'
import { scrollToTop } from '../lib'
import { MultiSelect } from './forms'

interface TenantProductFormProps {
  productID?: string;
  onSave?: (product: IProduct) => void;
  onEdit?: (product: IProduct) => void;
  tenantID: string;
}

const productFields = [
  { label: 'Term', name: 'term' },
  { label: 'Minimum Loan Amount', name: 'minLoanAmount' },
  { label: 'Maximum Loan Amount', name: 'maxLoanAmount' },
  { label: 'Interest Rate With Autopay', name: 'interestRateWithAutopay' },
  { label: 'Interest Rate Without Autopay', name: 'interestRateWithoutAutopay' },
  { label: 'Autopay APR', name: 'autoPayAPR' },
  { label: 'APR', name: 'apr' },
  { label: 'Payment Factor With Autopay', name: 'paymentFactorWithAutopay' },
  { label: 'Payment Factor Without Autopay', name: 'paymentFactorWithoutAutopay' },
  { label: 'Interest Only Period', name: 'interestOnlyPeriod' },
  { label: 'Dealer Fee', name: 'dealerFee' },
]

const productDateFields = [
  // referrals received after this date will be eligible for the product
  { label: 'Effective Date', name: 'effectiveDate', type: 'datetime-local' },
  // referrals received after this date will not be eligible for the product
  { label: 'Expiration Date', name: 'expirationDate', type: 'datetime-local' },
  // referrals that recevied before the expiration date but did not submit prequalification 
  // until after expiration will be honored until this date (if set)
  { label: 'Must Prequal By Date', name: 'prequalExpirationDate', type: 'datetime-local' },
]

const productTextAreaFields = [
  { label: 'Disclosures', name: 'disclosures' },
  { label: 'Product Description', name: 'productDescription' },
  { label: 'Detailed Product Description Client Html', name: 'detailedProductDescriptionClientHtml' },
  { label: 'Detailed Product Description Installer Html', name: 'detailedProductDescriptionInstallerHtml' }
]

const productSettingsFields = [
  { label: 'Decision Pro Product Name', name: 'decisionProProductName' }
]

const TenantProductForm: React.FC<TenantProductFormProps> = (
  { productID, onSave, tenantID, onEdit }) => {
  const [formData, setFormData] = useState({ ...initialTenantProduct })

  useEffect(() => {
    if (productID) {
      api.get(`/tenant/${tenantID}/products/${productID}`)
        .then(json => {
          setFormData({
            ...json,
            effectiveDate: json.effectiveDate ? new Date(json.effectiveDate) : null,
            expirationDate: json.expirationDate ? new Date(json.expirationDate) : null,
            prequalExpirationDate: json.prequalExpirationDate ? new Date(json.prequalExpirationDate) : null
          })
        })
        .catch(ex => {
          toast.error(`Error fetching product: ${ex.message}`)
        })
    }
  }, [productID, tenantID])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!formData.productType) {
      toast.error('Please select a product type')
      return
    }

    const path = `/tenant/${tenantID}/products/${productID || ''}`
    const method = productID ? api.patch(path, formData) : api.post(path, formData)

    method
      .then((response) => {
        if (productID && onEdit) {
          onEdit(response)
        } else if (onSave) {
          onSave(response)
        }
        toast.success(`Product ${productID ? 'updated' : 'saved'}`)
        scrollToTop()
      })
      .catch(error => {
        toast.error(`Error ${productID ? 'updating' : 'saving'} product: ${error.message}`)
      })
  }

  const handleInputChangeText = (e) => {
    const { name, value } = e.target
    setFormData(prevFormData => {
      return {
        ...prevFormData,
        [name]: value
      }
    })
  }

  const handleInputChangeNumeric = (e) => {
    const { name, value } = e.target
    setFormData(prevFormData => {
      const newValue = value.replace(/[^0-9.]/g, '')
      return {
        ...prevFormData,
        [name]: newValue
      }
    })
  }

  const handleInputChangeDate = (e) => {
    const { name, value } = e.target
    setFormData(prevFormData => {
      if (!value) {
        return {
          ...prevFormData,
          [name]: null
        }
      }

      // Create a date object that preserves the local time
      // The datetime-local input gives us YYYY-MM-DDTHH:MM in local time
      // We need to create a Date object that maintains that local time
      const [datePart, timePart] = value.split('T')
      const [year, month, day] = datePart.split('-').map(Number)
      const [hours, minutes] = timePart.split(':').map(Number)

      // Create date in local timezone (month is 0-indexed in Date constructor)
      const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0)

      return {
        ...prevFormData,
        [name]: localDate
      }
    })
  }

  const handleInputChangeCheckbox = (e) => {
    const { name, checked } = e.target
    setFormData(prevFormData => {
      return {
        ...prevFormData,
        [name]: checked
      }
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label>Product Type</label>
          <select
            name='productType'
            value={formData.productType || ''}
            onChange={handleInputChangeText}
            className="form-select form-control"
          >
            <option value="">Select a type</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="non-profit">Non-Profit</option>
          </select>
        </div>
        {productFields.map((field, index) => (
          <div key={index} className="col-md-6 mb-3">
            <label>{field.label}</label>
            <input
              type='text'
              required
              name={field.name}
              value={formData[field.name as keyof FormData]}
              className="form-control"
              onChange={handleInputChangeNumeric}
            />
          </div>
        ))}
      </div>

      <div className="row">
        {productDateFields.map((field, index) => {
          const value = formData[field.name as keyof FormData]
          return <div key={index} className="col-md-6 mb-3">
            <label>{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              value={value ? toDateTimeLocalInputString(value as Date) : ''}
              className="form-control"
              onChange={handleInputChangeDate}
            />
          </div>
        })}
        <div className="col-md-6 mb-3">
          <label>Is Valid for Incentive?</label>
          <input
            type='checkbox'
            name='isValidForIncentive'
            checked={formData.isValidForIncentive}
            onChange={handleInputChangeCheckbox}
          />
        </div>
      </div>
      <div className="form-group">
        <label>Available Equipment Types</label>
        <MultiSelect
          options={EQUIPMENT_TYPES_LABELS}
          values={formData.equipmentTypes || []}
          onChange={types => setFormData(prev => ({ ...prev, equipmentTypes: types }))}
          wrapStyle={{ display: 'flex' }}
        />
      </div>
      {productSettingsFields.map((field, index) => (
        <div key={index} className="form-group">
          <label>{field.label}</label>
          <input
            type='text'
            name={field.name}
            value={formData[field.name as keyof FormData] || ''}
            className="form-control"
            onChange={handleInputChangeText}
          />
        </div>
      ))}
      {productTextAreaFields.map((field, index) => (
        <div key={index} className="form-group">
          <label>{field.label}</label>
          <textarea
            name={field.name}
            value={formData[field.name as keyof FormData]}
            className="form-control"
            onChange={handleInputChangeText}
          />
        </div>
      ))}
      <button type="submit" className="btn btn-primary btn-sm">{productID ? 'Update' : 'Save'}</button>
    </form>
  )
}

export default TenantProductForm
